import React, { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '../styles/ThemeProvider';
import { SourceFileUpload } from '../components/SourceFileUpload';
import { MappingResultsView } from '../components/MappingResults';
import { TargetSpecType, MappingResults, LLMSettings, LLMJudgeMetrics, DEFAULT_LLM_SETTINGS } from '../types';
import { generateMapping } from '../services/mappingService';
import { validateFileContent } from '../services/guardrailsService';
import { api } from '../services/api';

const MainContent = styled.main`
  width: 100%;
  max-width: 1760px;
  margin: 0 auto;
  padding: 32px clamp(20px, 3vw, 48px);

  @media (max-width: 640px) {
    padding: 24px 16px;
  }
`;

const Title = styled.h2<{ color: string }>`
  font-size: 28px;
  font-weight: 600;
  color: ${props => props.color};
  margin-bottom: 8px;
`;

const Subtitle = styled.p<{ color: string }>`
  font-size: 16px;
  color: ${props => props.color};
  margin-bottom: 32px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingCard = styled.div`
  background: white;
  padding: 48px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`;

const Spinner = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border: 4px solid ${props => props.color}20;
  border-top-color: ${props => props.color};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p<{ color: string }>`
  font-size: 16px;
  color: ${props => props.color};
`;

const LoadingSub = styled.p<{ color: string }>`
  font-size: 13px;
  color: ${props => props.color};
  margin-top: 8px;
`;

const ErrorBanner = styled.div`
  background: #fff3f3;
  border: 1px solid #e53e3e;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 24px;
  color: #c53030;
  font-size: 14px;
`;

export const MappingPage: React.FC = () => {
  const theme = useTheme();
  const [file, setFile] = useState<File | null>(null);
  const [targetSpec, setTargetSpec] = useState<TargetSpecType>('claimstatus30');
  const [results, setResults] = useState<MappingResults | null>(null);
  const [isMapping, setIsMapping] = useState(false);
  const [llmSettings, setLlmSettings] = useState<LLMSettings>(DEFAULT_LLM_SETTINGS);
  const [judgeMetrics, setJudgeMetrics] = useState<LLMJudgeMetrics | null>(null);
  const [judgeLoading, setJudgeLoading] = useState(false);
  const [guardrailError, setGuardrailError] = useState<{ category: string; message: string } | null>(null);
  const [mappingError, setMappingError] = useState<string | null>(null);

  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    setResults(null);
    setGuardrailError(null);
    setMappingError(null);
    setJudgeMetrics(null);
  }, []);

  const handleTargetSpecChange = useCallback((selectedTargetSpec: TargetSpecType) => {
    setTargetSpec(selectedTargetSpec);
    setFile(null);
    setResults(null);
    setGuardrailError(null);
    setMappingError(null);
    setJudgeMetrics(null);
  }, []);

  const handleMap = useCallback(async () => {
    if (!file) return;

    setGuardrailError(null);
    setMappingError(null);
    setJudgeMetrics(null);

    // ── Client-side guardrail check ──────────────────────────────
    try {
      const content = await file.text();
      const guardrailResult = validateFileContent(content);
      if (!guardrailResult.passed) {
        setGuardrailError({ category: guardrailResult.category, message: guardrailResult.message });
        return;
      }

      setIsMapping(true);
      try {
        const mappingResults = await generateMapping(content, file.name, targetSpec, llmSettings);
        setResults(mappingResults);

        // ── Async LLM judge evaluation ─────────────────────────
        setJudgeLoading(true);
        api.judgeMapping(mappingResults).then(metrics => {
          setJudgeMetrics(metrics);
        }).catch(() => {
          // Judge failure is non-blocking
        }).finally(() => {
          setJudgeLoading(false);
        });
      } catch (err) {
        setMappingError(err instanceof Error ? err.message : 'Mapping failed. Please try again.');
      } finally {
        setIsMapping(false);
      }
    } catch {
      // If guardrail content read fails, attempt mapping without guardrail check
      setIsMapping(true);
      try {
        const content = await file.text();
        const mappingResults = await generateMapping(content, file.name, targetSpec, llmSettings);
        setResults(mappingResults);
      } catch (err) {
        setMappingError(err instanceof Error ? err.message : 'Mapping failed. Please try again.');
      } finally {
        setIsMapping(false);
      }
    }
  }, [file, targetSpec, llmSettings]);

  return (
    <>
      <MainContent>
        <Title color={theme.colors.textPrimary}>Map to HPS Data Load File</Title>
        {mappingError && (
          <ErrorBanner role="alert">
            <strong>Mapping failed:</strong> {mappingError}
          </ErrorBanner>
        )}
        <Subtitle color={theme.colors.textSecondary}>
          Select an HPS specification and upload only its matching HPS data-load file extension. The AI-powered
          engine will map fields to the selected HPS specification.
        </Subtitle>

        <SourceFileUpload
          targetSpec={targetSpec}
          onTargetSpecChange={handleTargetSpecChange}
          onFileSelect={handleFileSelect}
          selectedFile={file}
          onMap={handleMap}
          isMapping={isMapping}
          llmSettings={llmSettings}
          onSettingsChange={setLlmSettings}
          judgeMetrics={judgeMetrics}
          judgeLoading={judgeLoading}
          guardrailError={guardrailError}
        />

        {results && <MappingResultsView results={results} />}
      </MainContent>

      {isMapping && (
        <LoadingOverlay>
          <LoadingCard>
            <Spinner color={theme.colors.accentTeal} />
            <LoadingText color={theme.colors.textPrimary}>
              AI is analyzing your data...
            </LoadingText>
            <LoadingSub color={theme.colors.textMuted}>
              Detecting format, parsing fields, and matching to HPS spec
            </LoadingSub>
          </LoadingCard>
        </LoadingOverlay>
      )}
    </>
  );
};
